import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { DialogService } from 'primeng/dynamicdialog';

import { HelperService, HTTPService, ValidationService, DataService, GeoLocationService } from 'app/services';
import { AutoLeap } from 'app/constants/autoleap.constants';
import { userRoleConsts } from 'app/constants/userRoles.constants';
import { currency } from 'app/constants/currency.constants';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components/confirm-dismiss-dialog/confirm-dismiss-dialog.component';
import { ChangePasswordComponent } from 'app/modules/app-components/change-password/change-password.component';

import { findIndex, isEqual, lowerCase, trim } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
})
export class CreateUserComponent implements OnInit, OnDestroy {
  authRoles: any[] = [
    { name: 'Administrator', value: userRoleConsts.admin },
    { name: 'Technician', value: userRoleConsts.tech },
    { name: 'Service Advisor', value: userRoleConsts.serviceWriter },
  ];
  adminRoles: any[] = [userRoleConsts.admin, userRoleConsts.serviceWriter];
  roleMapper: any = {
    admin: 'Administrator',
    tech: 'Technician',
    serviceWriter: 'Service Advisor',
  };
  requiredField = {
    firstName: 'First name is required',
    lastName: 'Last name is required',
    phone: 'Phone is required',
    email: 'Email is required',
  };
  isSupportAdminAccount: boolean = false;
  isCheckingForUniqueness: boolean = false;
  roleChanged: boolean = false;
  role: any | string = '';
  createUpdateDialog: any;
  openedEmployeeRole: string = '';
  defaultPrivilegeState: any = [
    {
      privilegeId: null,
      title: 'dashboard',
      label: 'Dashboard',
      permitted: true,
      configurable: false,
      iconClass: 'fa-tachometer',
      isVisible: true,
      role: [userRoleConsts.admin, userRoleConsts.serviceWriter],
    },
    {
      privilegeId: null,
      title: 'reports',
      label: 'Reports',
      permitted: true,
      configurable: false,
      iconClass: 'fa-signal-4',
      isVisible: true,
      role: [userRoleConsts.admin, userRoleConsts.serviceWriter],
    },
    {
      privilegeId: null,
      title: 'restrict-ip-access',
      label: 'Restrict IP Access',
      permitted: false,
      configurable: true,
      iconClass: 'fa-regular fa-lock',
      isVisible: true,
      role: [userRoleConsts.tech, userRoleConsts.serviceWriter],
    },
    {
      privilegeId: null,
      title: 'settings',
      label: 'Settings',
      permitted: true,
      configurable: false,
      iconClass: 'fa-cog',
      isVisible: true,
      role: [userRoleConsts.admin, userRoleConsts.serviceWriter],
    },
    {
      privilegeId: null,
      title: 'assign-unassign-service',
      label: '',
      permitted: false,
      configurable: true,
      iconClass: 'fa-tools',
      isVisible: false,
      role: [userRoleConsts.tech, userRoleConsts.serviceWriter, userRoleConsts.admin],
    },
    {
      privilegeId: null,
      title: 'mark-delivered',
      label: 'Make changes to PO after they are marked "Delivered"',
      permitted: true,
      configurable: true,
      iconClass: 'fa-truck',
      isVisible: true,
      role: [userRoleConsts.admin, userRoleConsts.serviceWriter],
    },
  ];
  orderPrivilege = [
    {
      privilegeId: null,
      title: 'invoice-number-update',
      label: 'Update invoice number',
      permitted: true,
      configurable: false,
      iconClass: 'fal fa-file-alt',
      isVisible: true,
      role: [userRoleConsts.admin, userRoleConsts.serviceWriter],
    },
    {
      privilegeId: null,
      title: 'save-tags',
      label: 'Create/Update tags',
      permitted: false,
      configurable: true,
      iconClass: 'fal fa-tag',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'save-priorities',
      label: 'Create/Update priorities',
      permitted: false,
      configurable: true,
      iconClass: 'fal fa-sort-size-up',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'save-workflow',
      label: 'Create Workboard statuses',
      permitted: false,
      configurable: true,
      iconClass: 'fal fa-stream',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'save-end-session',
      label: "Allow user to end any user's active RO session",
      permitted: false,
      configurable: true,
      iconClass: 'fal fa-lock',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'revert-invoices',
      label: 'Unlock Invoices by switching to Estimate',
      permitted: false,
      configurable: false,
      iconClass: 'fal fa-lock',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'finalize-adjustment-invoice',
      label: 'Finalize Adjustment Repair Orders & Reopen Same-day Finalized Repair Orders',
      permitted: false,
      configurable: false,
      iconClass: 'font-16 mr-1 fal fa-usd-circle',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'restore-delete-ro',
      label: 'Restore and Permanently delete ROs from Trash',
      permitted: false,
      configurable: false,
      iconClass: 'fal fa-trash-alt',
      isVisible: false,
    },
  ];
  customerPrivilege = [
    {
      privilegeId: null,
      title: 'ar-account-management',
      label: 'AR account management',
      permitted: true,
      configurable: false,
      iconClass: 'fal fa-file-alt',
      isVisible: true,
    },
  ];
  pricingPrivilege = [
    {
      privilegeId: null,
      title: 'pricing-management',
      label: 'Manage Pricing for Catalog (Parts, Tires, Labor)',
      permitted: true,
      configurable: false,
      iconClass: 'fal fa-dollar-sign',
      isVisible: true,
    },
  ];
  paymentPrivilege = [
    {
      privilegeId: null,
      title: 'change-payment-date',
      label: 'Ability to change payment data',
      permitted: true,
      configurable: false,
      iconClass: 'fal fa-calendar-alt',
      isVisible: false,
    },
    {
      privilegeId: null,
      title: 'manage-payment-types-method',
      label: 'Manage company / shop level payment types and methods',
      permitted: true,
      configurable: false,
      iconClass: 'fal fa-address-card ml-neg-4',
      isVisible: false,
    },
  ];
  accountingPrivilege = [
    {
      privilegeId: null,
      title: 'mark-exported',
      label: 'Make changes to PO after they are marked as "Exported"',
      permitted: false,
      configurable: true,
      iconClass: 'fa-truck',
      isVisible: true,
      role: [userRoleConsts.admin, userRoleConsts.serviceWriter],
    },
    {
      privilegeId: null,
      title: 'accounting-exports',
      label: 'Ability to export accounting data',
      permitted: true,
      configurable: false,
      iconClass: 'fal fa-download',
      isVisible: false,
    },
  ];
  royaltyPrivilege = [
    {
      privilegeId: null,
      title: 'royalty-privileges',
      label: 'Royalty Fee Management',
      iconClass: 'fal fa-file-alt',
      permitted: true,
      configurable: true,
      isVisible: true,
    },
  ];
  catalogPrivilege = [];
  user: any = {
    role: { name: 'Administrator', value: 'admin' },
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    registrationNumber: '',
    rate: '',
    initials: '',
    notes: '',
    workAsTech: false,
    privileges: [],
    isWorkAsTechEnabled: false,
    isSupportUser: false,
    permissions: [
      {
        title: 'Allow Send Updates to Customer',
        permissionId: 0,
        selected: false,
      },
      {
        title: 'Chat with Customer',
        permissionId: 1,
        selected: false,
      },
      {
        title: 'Assign Technician',
        permissionId: 2,
        selected: false,
      },
      {
        title: 'Create Estimate',
        selected: false,
        permissionId: 3,
      },
      {
        title: 'Re-Open Estimate',
        selected: false,
        permissionId: 4,
      },
      {
        title: 'Update Estimate Status/Authorize Estimate',
        selected: false,
        permissionId: 5,
      },
      {
        title: 'Download Customer data',
        selected: false,
        permissionId: 6,
      },
      {
        title: 'Payment and Invoices Access',
        selected: false,
        permissionId: 7,
      },
      {
        title: 'Add/Edit Employees',
        selected: false,
        permissionId: 8,
      },
      {
        title: 'Change Pricing',
        selected: false,
        permissionId: 9,
      },
      {
        title: 'Dashboard',
        selected: false,
        permissionId: 10,
      },
      {
        title: 'Report',
        selected: false,
        permissionId: 11,
      },
      {
        title: 'Reviews',
        selected: false,
        permissionId: 12,
      },
      {
        title: 'Commision',
        selected: false,
        permissionId: 13,
      },
      {
        title: 'Restrict IP Access',
        selected: false,
        permissionId: 13,
      },
    ],
  };
  commissionPrivilege = {
    privilegeId: null,
    title: 'commission-plan',
    label: 'Commissions view / edit privileges',
    permitted: false,
    configurable: true,
    iconClass: 'fa-coins',
    isVisible: true,
    role: [userRoleConsts.serviceWriter, userRoleConsts.admin],
  };
  customerMandatoryFieldEditPrivilege = {
    privilegeId: null,
    title: 'customer-mandatory-field-edit',
    label: 'Edit Customer and Vehicle Mandatory fields',
    iconClass: 'fal fa-edit',
    permitted: true,
    configurable: true,
    isVisible: this.dataService.canUpdateCustVehMandatoryFields,
    role: [userRoleConsts.admin, userRoleConsts.serviceWriter, userRoleConsts.tech],
  };
  // Color scheme
  grayscalePrivilege = {
    privilegeId: null,
    title: 'gray-scale',
    label: 'Show user app in grayscale',
    permitted: false,
    configurable: true,
    iconClass: 'fa-palette',
    isVisible: true,
    role: [userRoleConsts.tech, userRoleConsts.serviceWriter, userRoleConsts.admin],
  };
  assignTechOptions: any = [
    { name: 'Technician cannot self assign', value: 'tech-cant-assign', label: 'tech-cant-assign' },
    { name: 'Technician can assign self to services with available roles', value: 'tech-available-role-assign', label: 'tech-available-role-assign' },
    { name: 'Technician can assign self to replace another technician on a service', value: 'tech-replace-tech', label: 'tech-replace-tech' },
  ];
  selectedPermissionCount: number = 0;
  errors: any = {};
  focus: any = {};
  title: string = '';
  userId: string = '';
  isPhoneUnique: boolean = true;
  isEmailUnique: boolean = true;
  isInitialsUnique: boolean = true;
  defaultUser: any = {};
  isUpdated: boolean = false;
  isOnboarding: boolean = false;
  isSuperUser: boolean = false;
  companyId: string = '';
  company: any = {};
  isCreatingUser: boolean = false;
  isUserSetupCompleted: boolean = false;
  activatedRouteSub: Subscription;
  isCurrentUser: boolean = false;
  dataServiceSubscription: Subscription;
  isManualInvCodeEnabled: boolean = false;
  isWorkAsTechEnabled: boolean = false;
  isCustomerVehicleMandatoryFieldsEnabled: boolean = false;
  isMultiLocationEnabled: boolean = false;
  isAutoLeapUser: boolean = false;
  companyLocations: any[] = [];
  shopAccesses: any[] = [];
  isEditMode: boolean = false;
  selectedPrivilege: any = {};
  isTechSelfAssignUnassignService: boolean = false;
  privileges: any[] = [];
  hasMultiLocationAccess: boolean = false;
  public userRoleConsts = userRoleConsts;
  commissionPlans: any = [];
  commissionPlanSuggestions: any = [];
  commissionPlanForServiceMetric: any = [];
  isCommissionPlanEnabled: boolean = false;
  usd: string = currency.USD;
  grayScaleChange: boolean = false;
  isGreyScaleThemeEnabled: boolean = false;
  isSupportUser: boolean = false;
  isInvoiceLockEnabled: boolean = false;
  isInvoiceLockOn: boolean = false;
  ordinaryAdmin: boolean;
  isProcessing: boolean = true;
  starterTierTechUserWarningMsg: string = '';
  isPermanentRODeleteEnabled: boolean = false;
  isArAccountManagement: boolean = false;
  isRevenueRecognitionSettleEnabled: boolean = false;
  isChargeAccountEnabled: boolean = false;
  isPricingManagementEnable: boolean = false;
  isPricingMatricesManagement: boolean = false;
  isSalesCodeEnabled: boolean = false;
  isNewItemCategoriesEnable: boolean = false;
  arAccountIconTooltip: string;
  pricingIconTooltip: string;
  paymentDataTooltip: string;
  catalogDataTooltip: string;
  catalogStockAdjustmentTooltip: string;
  catalogCreateUpdateSalesCodeTooltip: string;
  catalogInventoryPurchaseTooltip: string;
  isPaymentDataPrivilgesEnabled: boolean = false;
  isInventoryManagementEnabled: boolean = false;
  isAccountingExportsEnabled: boolean = false;
  isRoyaltyReportEnabled: boolean;
  isRoyaltyPermissionPrivileged: boolean = false;
  isPartWarrantiesEnabled: boolean = false;
  isTireWarrantyPolicyEnabled: boolean = false;
  restrictedIps = [{ ip: '', isValid: true, error: '' }];
  allowedIpsCount: number = 5;

  constructor(
    private helperService: HelperService,
    private httpService: HTTPService,
    private activatedRoute: ActivatedRoute,
    private validationService: ValidationService,
    private router: Router,
    private dialogService: DialogService,
    private dataService: DataService,
    private titleService: Title,
    private geoLocationService: GeoLocationService,
  ) {}

  async ngOnInit() {
    const { role, companyId, isRevenueRecognitionSettleEnabled } = this.helperService.getCompany();
    this.role = role;
    this.isRevenueRecognitionSettleEnabled = isRevenueRecognitionSettleEnabled;

    this.companyId = companyId;

    const revertOrderPrivilege = findIndex(this.orderPrivilege, { title: 'revert-invoices' });

    if (revertOrderPrivilege !== -1) {
      this.orderPrivilege[revertOrderPrivilege].configurable = this.role === 'admin';
    }

    this.activatedRouteSub = this.activatedRoute.data.subscribe((data) => {
      const { role, employeeId, hasMultiLocationAccess } = this.helperService.getCompany();
      const editUserRole = this.activatedRoute.snapshot.queryParamMap.get('role');

      if (editUserRole) {
        this.authRoles.forEach((el) => {
          if (el.value === editUserRole) {
            this.user.role = el;
          }
        });
      }

      this.isOnboarding = data.isOnboarding;
      this.isSuperUser = data.isSuperUser;
      this.isAutoLeapUser = data.isAutoLeapUser;
      this.isUserSetupCompleted = data.isUserSetupCompleted;

      if ([userRoleConsts.admin].includes(role)) {
        this.hasMultiLocationAccess = hasMultiLocationAccess;
      }

      this.setFeatureFlag();

      if (data.mode === 'edit') {
        this.userId = this.activatedRoute.snapshot.paramMap.get('id');
        this.isCurrentUser = employeeId === this.userId;
        this.isEditMode = true;

        this.fetchUserDetails();
      } else {
        this.title = 'Create new user';
        this.selectRole(this.user.role);
      }

      if (this.activatedRouteSub) {
        this.activatedRouteSub.unsubscribe();
      }
    });

    this.dataServiceSubscription = this.dataService.getData().subscribe(({ data }) => {
      if (data) {
        if (['user-privilege-updated', 'company-appdata-updated'].includes(data.key)) {
          this.setFeatureFlag();
        }
      }
    });

    if (this.companyId) {
      this.getCompanyDetails();
    }

    Promise.all([this.getLocation(), this.checkForSupportUser()]).finally(() => {
      this.isProcessing = false;
    });

    this.setPinnedTooltip();
    this.setPaymentDataTooltip();
    this.setPricingTooltip();
  }

  ngOnDestroy() {
    if (!!this.activatedRouteSub) {
      this.activatedRouteSub.unsubscribe();
    }

    if (this.dataServiceSubscription) {
      this.dataServiceSubscription.unsubscribe();
    }
  }

  onSelectSelfAssign(data, privilege) {
    if (data.value === 'tech-cant-assign') {
      this.selectedPrivilege[privilege.title] = { ...privilege, permitted: false, title: 'assign-unassign-service', label: 'Technician cannot self assign' };
      this.selectedPrivilege['self-Technician-service'] = {
        ...privilege,
        permitted: false,
        title: 'self-Technician-service',
        label: 'Technician can assign self to replace another technician on a service',
      };
    } else if (data.value === 'tech-available-role-assign') {
      this.selectedPrivilege[privilege.title] = { ...privilege, permitted: true, title: 'assign-unassign-service', label: 'Technician can assign self to services with available roles' };
      this.selectedPrivilege['self-Technician-service'] = {
        ...privilege,
        permitted: false,
        title: 'self-Technician-service',
        label: 'Technician can assign self to replace another technician on a service',
      };
    } else if (data.value === 'tech-replace-tech') {
      this.selectedPrivilege['self-Technician-service'] = {
        ...privilege,
        permitted: true,
        title: 'self-Technician-service',
        label: 'Technician can assign self to replace another technician on a service',
      };
      this.selectedPrivilege[privilege.title] = { ...privilege, permitted: true, title: 'assign-unassign-service', label: 'Technician can assign self to replace another technician on a service' };
    }
  }

  setDefaultLocation() {
    if (this.isEditMode) return;

    let currentShopId = localStorage.getItem('currentShop');

    if (!currentShopId) {
      if (this.dataService.currentShopId) {
        currentShopId = this.dataService.currentShopId;
      } else if (this.companyLocations.length) {
        const [shop] = this.companyLocations;
        currentShopId = shop._id;
      }
    }

    if (currentShopId) {
      this.shopAccesses = [currentShopId];
      this.user.defaultShopId = currentShopId;
      this.user.currentShopId = currentShopId;
    }
  }

  setFeatureFlag() {
    this.isRoyaltyPermissionPrivileged = this.dataService.isRoyaltyPermissionEnabled;
    this.isManualInvCodeEnabled = this.dataService.isManualInvCodeEnabled;
    this.isWorkAsTechEnabled = this.dataService.isWorkAsTechEnabled;
    this.isMultiLocationEnabled = this.dataService.isMultiLocationEnabled;
    this.isTechSelfAssignUnassignService = this.dataService.isTechSelfAssignUnassignService;
    this.isCommissionPlanEnabled = this.helperService.shouldDisplayCommissionPlan();
    this.isGreyScaleThemeEnabled = this.dataService.isGreyScaleThemeEnabled;
    this.isInvoiceLockEnabled = this.dataService.isInvoiceLockEnabled;
    this.isInvoiceLockOn = this.dataService.isInvoiceLockOn;
    this.isPermanentRODeleteEnabled = this.dataService.isPermanentRODeleteEnabled;
    this.isArAccountManagement = this.dataService.isArAccountManagement;
    this.isPaymentDataPrivilgesEnabled = this.dataService.isPaymentDataPrivilgesEnabled;
    this.isPricingMatricesManagement = this.dataService.isPricingMatricesManagement;
    this.isSalesCodeEnabled = this.dataService.isSalesCodeEnabled;
    this.isPartWarrantiesEnabled = this.dataService.isPartWarrantiesEnabled;
    this.isTireWarrantyPolicyEnabled = this.dataService.isTireWarrantyPolicyEnabled;
    this.isNewItemCategoriesEnable = this.dataService.isNewItemCategoriesEnable;
    this.isInventoryManagementEnabled = this.dataService.isInventoryManagementEnabled;
    this.isAccountingExportsEnabled = this.dataService.isAccountingExportsEnabled;
    this.isRoyaltyReportEnabled = this.dataService.isRoyaltyReportEnabled;
    this.isCustomerVehicleMandatoryFieldsEnabled = this.dataService.isCustomerVehicleMandatoryFieldsEnabled;

    this.setCatalogDataTooltip();

    this.paymentPrivilege.forEach((privilege) => {
      if (privilege.title === 'change-payment-date') {
        privilege.isVisible = this.dataService.changePaymentData;
      } else {
        privilege.isVisible = this.dataService.managePaymentTypesAndMethods;
      }
    });

    this.accountingPrivilege.forEach((privilege) => {
      if (privilege.title === 'accounting-exports') {
        privilege.isVisible = true;
      }
    });

    if (findIndex(this.catalogPrivilege, { title: 'catalog-classficiation' }) === -1 && this.isNewItemCategoriesEnable) {
      const manageStockPrivilege = {
        privilegeId: null,
        title: 'catalog-classficiation',
        label: 'Catalog Classification Management',
        permitted: true,
        configurable: false,
        iconClass: 'fal fa-tag',
        isVisible: false,
      };
      this.catalogPrivilege.push(manageStockPrivilege);
    }

    if (findIndex(this.catalogPrivilege, { title: 'create-update-salescode' }) === -1 && this.isNewItemCategoriesEnable && this.isSalesCodeEnabled) {
      const createUpdateSalesCodePrivilege = {
        privilegeId: null,
        title: 'create-update-salescode',
        label: 'Create/Update Sales Codes and Groups',
        permitted: true,
        configurable: true,
        iconClass: 'fal fa-tag',
        isVisible: false,
      };
      this.catalogPrivilege.push(createUpdateSalesCodePrivilege);
    }

    if (findIndex(this.catalogPrivilege, { title: 'manage-stock-adjustment' }) === -1 && this.isInventoryManagementEnabled) {
      const manageStockPrivilege = {
        privilegeId: null,
        title: 'manage-stock-adjustment',
        label: 'Manual stock adjustment',
        permitted: true,
        configurable: false,
        iconClass: 'fal fa-tag',
        isVisible: false,
      };
      this.catalogPrivilege.push(manageStockPrivilege);
    }

    if (findIndex(this.catalogPrivilege, { title: 'update-inventory-PO' }) === -1) {
      const updateInventoryPrivilege = {
        privilegeId: null,
        title: 'update-inventory-PO',
        label: 'Create/update Inventory POs',
        permitted: true,
        configurable: true,
        iconClass: 'fal fa-tag',
        isVisible: true,
      };
      this.catalogPrivilege.push(updateInventoryPrivilege);
    }

    this.catalogPrivilege.forEach((privilege) => {
      if (privilege.title === 'catalog-classficiation' && this.isNewItemCategoriesEnable) {
        privilege.isVisible = this.dataService.canEditClassifications;
      }

      if (privilege.title === 'create-update-salescode' && this.isNewItemCategoriesEnable && this.isSalesCodeEnabled) {
        privilege.isVisible = this.dataService.canEditSalesCode;
      }

      if (privilege.title === 'update-inventory-PO') {
        privilege.isVisible = this.dataService.isUpdateInventoryPO;
      }

      if (privilege.title === 'manage-stock-adjustment' && this.isInventoryManagementEnabled) {
        privilege.isVisible = this.dataService.canUpdateStockManually;
      }
    });

    if (this.isSuperUser || this.isAutoLeapUser) {
      this.user.hasMultiLocationAccess = true;
    }

    if (this.isMultiLocationEnabled) {
      this.orderPrivilege.forEach((privilege: any) => {
        if (['save-tags', 'save-priorities', 'save-workflow'].includes(privilege.title)) {
          privilege.isVisible = this.hasMultiLocationAccess;
        }
      });
    }

    if (this.dataService.isLockOrderEnabled) {
      const index = findIndex(this.orderPrivilege, { title: 'save-end-session' });

      if (index !== -1) {
        this.orderPrivilege[index].isVisible = true;
      }
    }

    if (this.dataService.isPermanentRODeleteEnabled) {
      const index = findIndex(this.orderPrivilege, { title: 'restore-delete-ro' });

      if (index !== -1) {
        this.orderPrivilege[index].isVisible = true;
        this.selectRole(this.user.role);
      }
    }

    const { isRevenueRecognitionSettleEnabled } = this.helperService.getCompany();

    if (isRevenueRecognitionSettleEnabled && this.dataService.isAdjustmentInvoicesEnabled) {
      const index = findIndex(this.orderPrivilege, { title: 'finalize-adjustment-invoice' });

      if (index !== -1) {
        this.orderPrivilege[index].isVisible = true;
        this.selectRole(this.user.role);
      }
    }

    if (this.isInvoiceLockOn && this.isInvoiceLockEnabled) {
      const index = findIndex(this.orderPrivilege, { title: 'revert-invoices' });

      if (index !== -1) {
        this.orderPrivilege[index].isVisible = true;
      }
    }

    if (this.isCommissionPlanEnabled && findIndex(this.defaultPrivilegeState, { title: this.commissionPrivilege.title }) === -1) {
      this.defaultPrivilegeState.push(this.commissionPrivilege);
      this.getCommisionList();
    }

    if (this.isGreyScaleThemeEnabled && findIndex(this.defaultPrivilegeState, { title: this.grayscalePrivilege.title }) === -1) {
      this.defaultPrivilegeState.push(this.grayscalePrivilege);
    }

    const customerMandatoryFieldPrivilegeIndex = findIndex(this.defaultPrivilegeState, { title: this.customerMandatoryFieldEditPrivilege.title });
    if (this.dataService.canUpdateCustVehMandatoryFields && this.isCustomerVehicleMandatoryFieldsEnabled && customerMandatoryFieldPrivilegeIndex === -1) {
      this.defaultPrivilegeState.push(this.customerMandatoryFieldEditPrivilege);
    }

    this.defaultPrivilegeState = [...this.defaultPrivilegeState];
  }

  private async checkForSupportUser(): Promise<void> {
    try {
      const data: any = await this.httpService.isSupportAdminAccount();
      this.isSupportAdminAccount = data;
    } catch (error) {
      this.helperService.showToaster('error', error);
    }
  }

  getCompanyDetails() {
    this.httpService.getCompaniesByAdmin(this.companyId).then(
      (company) => {
        this.dataService.sendData({
          type: 'onboarding',
          companyId: this.companyId,
          company,
        });
        this.company = company;

        if (company.appsettings) {
          this.isMultiLocationEnabled = company.appsettings.isMultiLocationEnabled;
          this.isWorkAsTechEnabled = company.appsettings.isWorkAsTechEnabled;
          this.isChargeAccountEnabled = company.appsettings.isChargeAccountEnabled;
          this.isPricingManagementEnable = company.appsettings.isPricingManagementEnable;
        }

        if (this.isOnboarding) {
          this.user.role = { name: 'Administrator', value: 'admin' };
          if (this.isSuperUser) {
            this.handleSelectAll(0);

            if (company.superUserId) {
              this.userId = company.superUserId._id;
              this.fetchUserDetails();
            }
          }

          if (this.isAutoLeapUser && company.autoleapUserId) {
            this.userId = company.autoleapUserId._id;
            this.fetchUserDetails();
          }

          if (this.isUserSetupCompleted && company.otherUserId) {
            this.userId = company.otherUserId._id;
            this.fetchUserDetails();
          }
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      },
    );
  }

  async getLocation() {
    try {
      const { records = [] } = await this.httpService.getShopLists({ active: true, sortField: 'name', sortOrder: -1 });

      this.companyLocations = records;

      this.setDefaultLocation();
    } catch (err) {
      this.helperService.showToaster('error', err);
    }
  }
  isSupportUserCheck(email) {
    const supportUserEmailExpression = /^alsupport\.[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@autoleap\.com$/;

    return !!(email && supportUserEmailExpression.test(email.toLowerCase()));
  }

  async fetchUserDetails() {
    const privilegeMap = {};
    let user = <any>{};

    if (![userRoleConsts.super, userRoleConsts.onboarder, userRoleConsts.engg, userRoleConsts.sales].includes(this.role)) {
      user = await this.httpService.getEmployeeDetails(this.userId);
    } else {
      user = await this.httpService.getCompanyEmployeeByAdmin(this.companyId, this.userId);
    }
    this.isSupportUser = this.isSupportUserCheck(user.email);

    this.user = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: this.helperService.formatPhoneNumber(user.phone),
      registrationNumber: user.registrationNumber,
      rate: user.rate,
      initials: user.initials,
      isInActive: user.isInActive,
      userId: user.userId,
      privileges: [],
      isWorkAsTechEnabled: user.isWorkAsTechEnabled,
      currentShopId: user.currentShopId,
      defaultShopId: '',
      shopAccesses: (user.shopAccesses || []).map(({ shopId }) => shopId),
      hasMultiLocationAccess: user.hasMultiLocationAccess || false,
      employeeId: user.userId,
    };

    if (user.role === 'admin' && !this.hasMultiLocationAccess) {
      this.ordinaryAdmin = true;
    }

    this.user.commissionAdvisor = user.commissionAdvisor;
    this.user.commissionTech = user.commissionTech;

    const [defaultShop] = user.shopAccesses.filter(({ isDefault }) => isDefault);

    if (defaultShop) {
      this.user.defaultShopId = defaultShop.shopId;
    } else {
      this.user.defaultShopId = user.currentShopId;
    }

    this.shopAccesses = this.user.shopAccesses;
    this.openedEmployeeRole = user.role;

    if (user.privileges?.length) {
      user.privileges.forEach((p: any) => {
        privilegeMap[p.title] = p;
        this.selectedPrivilege[p.title] = p;
        if (p.title === 'restrict-ip-access') {
          this.restrictedIps = p.allowedIps.map((el) => {
            return { ip: el, isValid: true, error: '' };
          });
        }
      });
    }

    for (let i = 0; i < this.authRoles.length; i++) {
      if (user.role === this.authRoles[i].value) {
        this.user.role = this.authRoles[i];
        break;
      }
    }

    this.selectRole(this.user.role, privilegeMap);

    this.user.permissions = this.user.permissions;

    if (user.permissions && user.permissions.length) {
      this.user.permissions = user.permissions;
      user.permissions.forEach(({ selected }) => {
        if (selected) {
          this.selectedPermissionCount += 1;
        }
      });
    }

    this.title = `Edit ${user.firstName} ${user.lastName}`;
    this.defaultUser = this.helperService.deepCopy(this.user);
  }

  handleSearchCommission(type: string, event: any) {
    if (this.commissionPlans) {
      if (type === 'nontechnician') {
        this.commissionPlanSuggestions = this.commissionPlans.filter((plan: any) => {
          return lowerCase(plan.name).includes(lowerCase(event));
        });
      } else if (type === 'technician') {
        this.commissionPlanForServiceMetric = this.commissionPlans.filter((item: any) => {
          return item.metricType === 'serviceMetric' && lowerCase(item.name).includes(lowerCase(event));
        });
      }
    }
  }

  handleClearSearch(type: string) {
    if (type === 'nontechnician') {
      this.commissionPlanSuggestions = this.helperService.deepCopy(this.commissionPlans);
    } else if (type === 'technician') {
      this.commissionPlanForServiceMetric = this.commissionPlans.filter((item) => {
        return item.metricType === 'serviceMetric';
      });
    }
  }

  getAvailableRoles() {
    if (this.company?.tierType && this.company.tierType.toLowerCase() === 'starter') {
      return this.authRoles.filter((role) => role.value !== 'tech');
    }
    return this.authRoles;
  }

  openConfirmationDialog(type: string) {
    const userType: string = type === 'commissionTech' ? 'Tech' : 'Service Advisor';
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        headerIcon: 'fa fa-exclamation-triangle font-icon-red',
        title: 'Disassociate commission plan?',
        message: `Disassociating commission plan for ${userType} user will lead to no calculation of commission as a ${userType} user. Are you sure you want to continue?`,
        button: {
          success: {
            title: 'Yes',
            backgroundColor: '#D47877',
          },
          cancel: { title: 'No' },
        },
      },
    });

    const onClose = ref.onClose.subscribe((close: any) => {
      if (close.isSuccess) {
        this.removePlan(type);
      }

      onClose.unsubscribe();
    });
  }

  handleChange(name: string, value: any) {
    if (name === 'defaultShopId' && !this.shopAccesses.includes(value)) {
      return;
    }

    this.user[name] = value;
    this.checkForChange();
  }

  handleFocus(name: string) {
    this.errors[name] = '';
    this.focus[name] = true;
  }

  handleBlur(name: string) {
    const value = this.user[name];
    this.focus[name] = false;
    if (name === 'firstName' && !value) {
      this.errors[name] = 'First Name is required';
    }

    if (name === 'lastName' && !value) {
      this.errors[name] = 'Last Name is required';
    }

    if (name === 'email') {
      if (!value) {
        // if (this.user.role.value === 'tech' && this.company?.tierType === 'starter') return;
        this.errors[name] = 'Email is required';
      }

      if (value && !this.validationService.validatingEmail(value)) {
        if (value !== this.defaultUser.email) {
          this.checkForUniqueness(name, value);
        }
      } else if (value) {
        this.errors.email = 'Email is invalid';
      }
    }

    if (name === 'phone') {
      if (!value) {
        this.errors[name] = 'Phone is required';
      }
      if (value) {
        const phone = this.helperService.removePhoneFormatting(value);
        const defaultPhone = this.helperService.removePhoneFormatting(this.defaultUser.phone);
        if (phone.length === 10) {
          if (phone !== defaultPhone) {
            this.checkForUniqueness(name, phone);
          } else {
            this.setUniquenessFlag('phone', true);
          }
        } else {
          this.errors[name] = 'Phone is invalid e.g. (000) 000-0000';
        }
      }
    }

    if (name === 'initials') {
      if (!value) {
        this.errors[name] = 'Initials are required';
      }

      if (value) {
        const initials = this.user.initials;
        if (initials.length === 2) {
          if (initials !== this.defaultUser.initials) {
            this.checkForUniqueness(name, initials);
          }
        } else {
          this.errors[name] = 'Min 1 character, Max 2 character';
        }
      }
    }
  }

  handlTechSettingChange(key: string, value: any) {
    this.user[key] = value;
  }

  selectRole(role: any, privilege: any = {}) {
    this.ordinaryAdmin = false;
    this.starterTierTechUserWarningMsg = '';
    this.user.role = role;

    const markDeliveredIndex = findIndex(this.defaultPrivilegeState, { title: 'mark-delivered' });
    const accountingExport = findIndex(this.accountingPrivilege, { title: 'accounting-exports' });

    if (this.user.role.value === 'serviceWriter' && !this.isSupportAdminAccount) {
      if (markDeliveredIndex !== -1) {
        this.defaultPrivilegeState[markDeliveredIndex].permitted = false;
      }
      if (accountingExport !== -1) {
        this.accountingPrivilege[accountingExport].permitted = false;
        this.accountingPrivilege[accountingExport].isVisible = false;
        this.selectedPrivilege['accounting-exports'] = this.accountingPrivilege[accountingExport];
      }
    } else if (this.user.role.value === 'admin') {
      if (markDeliveredIndex !== -1) {
        this.defaultPrivilegeState[markDeliveredIndex].permitted = true;
      }
      if (accountingExport !== -1) {
        this.accountingPrivilege[accountingExport].permitted = true;
        this.accountingPrivilege[accountingExport].isVisible = true;
      }

      if (this.user.role.value === 'admin' && !this.hasMultiLocationAccess) {
        this.ordinaryAdmin = true;
      }
    }

    if (role.value === 'tech' && this.company?.tierType === 'starter') {
      this.starterTierTechUserWarningMsg = "Technicians in the Starter Tier don't have access to the Technician app. Please contact AutoLeap Support for further information.";
    }

    this.getPrivilege(role.value, privilege);
    this.checkForChange();
  }

  isSupportEmail() {
    return new RegExp(/alsupport..+@autoleap.com$/).test(this.user.email);
  }

  checkForUniqueness(name: string, value: any) {
    this.isCheckingForUniqueness = true;
    this.httpService
      .isUniqueEmployee(name, value)
      .then(
        (res: boolean) => {
          this.setUniquenessFlag(name, res);
        },
        (err) => {
          this.setUniquenessFlag(name, err);
        },
      )
      .finally(() => {
        this.isCheckingForUniqueness = false;
      });
  }

  setUniquenessFlag(name: string, isUnique: boolean) {
    if (name === 'email') {
      if (isUnique) {
        this.isEmailUnique = true;
        this.errors[name] = '';
      } else {
        this.isEmailUnique = false;
        this.errors[name] = 'Email already exist';
      }
    }

    if (name === 'phone') {
      if (isUnique) {
        this.isPhoneUnique = true;
        this.errors[name] = '';
      } else {
        this.isPhoneUnique = false;
        this.errors[name] = 'Phone already exist';
      }
    }

    if (name === 'initials') {
      if (isUnique) {
        this.isInitialsUnique = true;
        this.errors[name] = '';
      } else {
        this.isInitialsUnique = false;
        this.errors[name] = 'Initials already exist';
      }
    }
  }

  handleSwitchChange(type: string, isInActive: boolean) {
    if (type === 'user-switch') {
      this.user.isInActive = !isInActive;
    }
    if (type === 'technician-switch') {
      this.user.isHybridUser = isInActive;
      // if(this.commissionPlans.length > 0) {
      //   this.user.commissionTech = this.helperService.deepCopy(this.commissionPlans[0]);
      // }

      // if(!this.user.isHybridUser && !this.commissionPlans.length) {
      //   this.user.commissionTech = {};
      // }
    }
    this.checkForChange();
  }

  checkForChange() {
    this.isUpdated = !isEqual(this.defaultUser, this.user);
  }
  removePlan(type: string) {
    if (type === 'commissionAdvisor') {
      if (this.user.commissionAdvisor) {
        this.user.commissionAdvisor = {};
      }
    } else if (type === 'commissionTech') {
      if (this.user.commissionTech) {
        this.user.commissionTech = {};
      }
    }
  }

  getCommisionList() {
    this.httpService.getCommissionPlans().then(
      (res: any) => {
        if (res && res.length > 0) {
          this.commissionPlans = res;
          this.commissionPlans = this.commissionPlans.filter((item) => item.active);
          this.commissionPlanSuggestions = this.helperService.deepCopy(this.commissionPlans);
          this.commissionPlanForServiceMetric = this.commissionPlans.filter((item) => item.metricType === 'serviceMetric');
          // if (this.commissionPlans.length > 0) {
          //   this.user.commissionAdvisor = this.helperService.deepCopy(this.commissionPlans[0]);
          // }
        } else {
          this.commissionPlans = [];
        }
      },
      (err) => {
        this.commissionPlans = [];
        this.helperService.showToaster('error', err);
      },
    );
  }

  async create(employee: any) {
    if (this.isCreatingUser) return;
    this.isCreatingUser = true;

    try {
      const employeeWithGeoInfo = { ...employee };

      try {
        const geoInfo = await this.geoLocationService.getGeoInfo();
        employeeWithGeoInfo.geoInfo = geoInfo;
      } catch (err) {
        console.log(err);
      }

      await this.httpService.createEmployee(employeeWithGeoInfo);

      this.helperService.showToaster('success', 'Employee Created');
      this.router.navigate(['settings/users/list']);
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isCreatingUser = false;
    }
  }

  async update(employee: any) {
    if (this.isCreatingUser) return;
    this.isCreatingUser = true;

    try {
      await this.httpService.updateEmployeeDetails({ ...employee, _id: this.userId });

      const { userId, employeeId } = this.helperService.getCompany();

      if (this.userId === employeeId) {
        this.dataService.sendData(this.dataService.typePassData('re-evaluate-privilleges', { employeeId }));
      }
      this.helperService.showToaster('success', 'Employee Updated');
      this.router.navigate(['settings/users/list']);

      if (this.grayScaleChange) {
        window.location.reload();
      }
      if (!this.defaultUser.isWorkAsTechEnabled && this.user.isWorkAsTechEnabled && userId === this.user.employeeId) {
        this.titleService.setTitle(AutoLeap);
        this.helperService.logout();
      }
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isCreatingUser = false;
    }
  }

  validation() {
    let isValid = true;
    Object.keys(this.requiredField).forEach((key) => {
      if (!this.user[key] && this.requiredField[key]) {
        this.errors[key] = this.requiredField[key];
        isValid = false;
      }
    });

    if (this.user.email && this.validationService.validatingEmail(this.user.email)) {
      this.errors.email = 'Email is invalid';
      isValid = false;
    }

    if (!this.user.email) {
      // email is not required if Role is tech and tierType is starter.
      // if (this.user.role.value === 'tech' && this.company?.tierType === 'starter') {
      //   this.errors.email = '';
      // } else {
      this.errors.email = 'Email is required';
      isValid = false;
      // }
    }

    if (this.user.phone && this.helperService.removePhoneFormatting(this.user.phone).length !== 10) {
      this.errors.phone = 'Phone is invalid e.g. (000) 000-0000';
      isValid = false;
    }

    //Adding a fix for ALP-19213
    if ((this.user.role.value === 'tech' || this.user.isWorkAsTechEnabled) && !trim(this.user.rate)) {
      this.errors.rate = 'Hourly rate is required';
      isValid = false;
    }

    if ((this.user.role.value === 'tech' || this.user.isWorkAsTechEnabled) && !this.user.initials) {
      this.errors.initials = 'Initials are required';
      isValid = false;
    }

    // check if ips array is not empty
    const restrictIpPrivilege = this.selectedPrivilege['restrict-ip-access'];
    if (restrictIpPrivilege?.permitted) {
      const hasInvalidIp = this.restrictedIps.some((item) => !item.isValid);
      const hasEmptyIp = this.restrictedIps.some((item) => !item.ip.trim());

      if (!this.restrictedIps.length) {
        // Case 1: If array is empty, add an empty field with an error
        this.restrictedIps.push({ ip: '', isValid: false, error: 'IP Address is required' });
        isValid = false;
      } else if (hasEmptyIp) {
        // Case 2: If there are items but some are empty, ask user to fill them
        this.restrictedIps.forEach((item) => {
          if (!item.ip.trim()) {
            item.isValid = false;
            item.error = 'Please enter an IP address';
          }
        });
        isValid = false;
      } else if (hasInvalidIp) {
        // Case 3: If any item is invalid, return error but don't add an empty field
        isValid = false;
      }
    }

    return isValid && this.isEmailUnique && this.isPhoneUnique && this.isInitialsUnique;
  }

  handleCheckboxChange(accessIndex: number, isChecked: boolean) {
    this.user.permissions[accessIndex].selected = isChecked;

    if (isChecked) {
      this.selectedPermissionCount += 1;
    } else {
      this.selectedPermissionCount -= 1;
    }

    this.checkForChange();
  }

  handleShopAccessChange(shopId: string, isAdded: boolean) {
    if (isAdded) {
      this.shopAccesses = [...this.shopAccesses, shopId];
    } else {
      this.shopAccesses = [...this.shopAccesses.filter((id) => id !== shopId)];
    }
  }

  handleSelectAll(checkState: number) {
    const check = checkState === 0 || checkState === 2;
    this.user.permissions.forEach((permission) => (permission.selected = check));
    this.selectedPermissionCount = check ? this.user.permissions.length : 0;

    this.checkForChange();
  }

  handleGroupSelect(name: string, checkState: number) {
    const check = checkState === 0 || checkState === 2;

    if (name === 'settings') {
      this.user.permissions[8].selected = check;
      this.user.permissions[9].selected = check;
    }

    if (name === 'communication') {
      this.user.permissions[0].selected = check;
      this.user.permissions[1].selected = check;
    }

    if (name === 'estimate') {
      this.user.permissions[2].selected = check;
      this.user.permissions[3].selected = check;
      this.user.permissions[4].selected = check;
      this.user.permissions[5].selected = check;
    }

    if (name === 'list') {
      this.user.permissions[6].selected = check;
      this.user.permissions[7].selected = check;
    }
    this.checkForChange();
  }

  groupCheckState(name) {
    const permissions = this.user.permissions;
    if (this.selectedPermissionCount === 0) {
      return 0;
    }

    if (this.selectedPermissionCount === this.user.permissions.length) {
      return 1;
    }

    if (name === 'communication') {
      if (permissions[0].selected && permissions[1].selected) {
        return 1;
      } else if (permissions[0].selected || permissions[1].selected) {
        return 2;
      } else {
        return 0;
      }
    }

    if (name === 'estimate') {
      if (permissions[2].selected && permissions[3].selected && permissions[4].selected && permissions[5].selected) {
        return 1;
      } else if (permissions[2].selected || permissions[3].selected || permissions[4].selected || permissions[5].selected) {
        return 2;
      } else {
        return 0;
      }
    }

    if (name === 'list') {
      if (permissions[6].selected && permissions[7].selected) {
        return 1;
      } else if (permissions[6].selected || permissions[7].selected) {
        return 2;
      } else {
        return 0;
      }
    }

    if (name === 'settings') {
      if (permissions[8].selected && permissions[9].selected) {
        return 1;
      } else if (permissions[8].selected || permissions[9].selected) {
        return 2;
      } else {
        return 0;
      }
    }
    this.checkForChange();
  }

  handleDiscardChange() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Discard data`,
        message: `The entered data will be permanently discarded.`,
        button: {
          success: { title: 'Confirm' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.router.navigate(['settings/users/list']);
      }
      onClose.unsubscribe();
    });
  }

  handleSaveChange() {
    this.createUpdateDialog = null;
    if (!this.validation()) {
      return;
    }

    let title = this.userId ? 'Update user' : 'Create user';
    let message = this.userId ? `Are you sure you want to update this user?` : 'Are you sure you want to create this user?';

    // edit mode & role update
    if (this.userId && this.openedEmployeeRole !== this.user.role.value) {
      title = `Update the Role for ${this.user.firstName}`;
      message = `User will be immediately logged out from their current session as ${this.roleMapper[this.openedEmployeeRole]} \
      which may cause loss of unsaved changes. When the user logs in they will be logged in as \
      ${this.roleMapper[this.user.role.value]} with updated privileges. Are you sure you want to update the role?`;
      this.roleChanged = true;
    }

    if (!this.createUpdateDialog) {
      this.createUpdateDialog = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          styleClassHeading: 'font-14',
          title,
          message,
          button: {
            success: { title: 'Confirm' },
            cancel: { title: 'Cancel' },
          },
        },
      });
    }

    const onClose = this.createUpdateDialog.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.saveUser();
      }
      onClose.unsubscribe();
    });
  }

  saveUser() {
    const employee: any = {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.email,
      phone: this.helperService.removePhoneFormatting(this.user.phone),
      registrationNumber: this.user.registrationNumber,
      rate: this.user.rate,
      initials: this.user.initials,
      role: this.user.role.value,
      permissions: this.user.permissions,
      privileges: [],
      isInActive: this.user.isInActive,
      currentShopId: this.user.currentShopId,
      isWorkAsTechEnabled: this.user.isWorkAsTechEnabled,
      hasMultiLocationAccess: this.user.hasMultiLocationAccess,
      shopAccesses: [],
    };

    if (this.isCommissionPlanEnabled) {
      if (this.user.commissionAdvisor && this.user.commissionAdvisor.name && this.adminRoles.includes(this.user.role.value)) {
        employee.commissionAdvisor = this.user.commissionAdvisor;
      } else {
        employee.commissionAdvisor = {};
      }

      if (this.user.isWorkAsTechEnabled || !this.adminRoles.includes(this.user.role.value)) {
        if (this.user.commissionTech && this.user.commissionTech.name) {
          employee.commissionTech = this.user.commissionTech;
        } else {
          employee.commissionTech = {};
        }
      } else {
        employee.commissionTech = {};
      }
    }

    if (this.shopAccesses) {
      employee.shopAccesses = this.shopAccesses.map((shopId) => ({
        shopId,
        hasAccess: true,
        isDefault: this.user.defaultShopId === shopId,
      }));
    }

    if (this.isAutoLeapUser && !new RegExp(/alsupport..+@autoleap.com$/).test(employee.email)) {
      this.errors.email = `Email is invalid. e.g. alsupport.xxxxxxx@autoleap.com`;
      return;
    }

    if (this.selectedPrivilege) {
      Object.keys(this.selectedPrivilege).forEach((key: string) => {
        if (!(this.user.role.value === userRoleConsts.admin && key === 'restrict-ip-access')) {
          employee.privileges.push(this.selectedPrivilege[key]);
        }
      });

      // ----- add restricted ips -----
      employee.privileges = employee.privileges.map((el) => {
        if (el.title === 'restrict-ip-access') {
          el.allowedIps = this.restrictedIps.map((item) => item.ip).flat();
        }
        return el;
      });
    }

    if (this.user.role.value === 'admin') {
      employee.privileges = employee.privileges.map((privilege) => {
        if (privilege.label === 'Dashboard' || privilege.label === 'Reports' || privilege.label === 'Settings') {
          // replace "name" property for objects with id 2 and 4
          return { ...privilege, permitted: true };
        } else {
          return privilege;
        }
      });
    }

    if (employee.role === 'owner') {
      employee.isInActive = false;
    }

    if (this.userId) {
      employee['userId'] = this.user.userId;
      this.update(employee);
    } else {
      this.create(employee);
    }
  }

  /**
   * Change users password
   */
  changePassword(): void {
    this.dialogService.open(ChangePasswordComponent, {
      width: '50%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: { userId: this.userId, isOnboarding: this.isAutoLeapUser },
    });
  }

  handlePrivilegeChange(privilege: any, permitted: boolean): void {
    this.selectedPrivilege[privilege.title] = { ...privilege, permitted };
    if (privilege.title === 'gray-scale') {
      this.grayScaleChange = true;
    }
  }

  /**
   * Get role privileges
   */
  getPrivilege(role: string, userPrivileges: any = {}): void {
    this.httpService
      .getRolePrivileges({ role })
      .then((data) => {
        const privilegeMap = {};

        data.privileges = data.privileges || [];
        data.privileges.forEach((p: any) => {
          privilegeMap[p.title] = p;
        });

        this.privileges = this.defaultPrivilegeState
          .filter(({ role: privilegeRole = [] }) => privilegeRole.includes(role))
          .map((p: any) => {
            if (privilegeMap[p.title] && p.title !== 'invoice-number-update') {
              p.privilegeId = privilegeMap[p.title].privilegeId;
              p.configurable = privilegeMap[p.title].configurable;
              p.permitted = privilegeMap[p.title].permitted;
            }

            if (!this.userId) {
              this.selectedPrivilege[p.title] = p;
            }

            return p;
          });

        if (this.orderPrivilege) {
          this.orderPrivilege.forEach((privilege: any) => {
            if (privilegeMap[privilege.title]) {
              privilege.privilegeId = privilegeMap[privilege.title].privilegeId;
              privilege.configurable = privilegeMap[privilege.title].configurable;
              privilege.permitted = privilegeMap[privilege.title].permitted;
            }

            if (privilege.title === 'revert-invoices') {
              if (this.role === 'admin' && this.user.role.value === 'admin') {
                privilege.configurable = true;
              }
            }

            if (this.isPermanentRODeleteEnabled && privilege.title === 'restore-delete-ro') {
              if (this.user.role.value !== userRoleConsts.admin) {
                privilege.permitted = false;
              } else {
                privilege.permitted = true;
              }

              if (this.role === 'admin' && this.user.role.value === 'admin') {
                privilege.configurable = true;
              }
            }
            //Adding the default value to true fro admins for finalizae-adjustment-invoices ALP-20580
            if(privilege.title === 'finalize-adjustment-invoice') {
              if(this.role === 'admin' && this.user.role.value === 'admin') {
                privilege.permitted = true;
              } else {
                privilege.permitted = false;
              }
            }
            

            if (this.dataService.isAdjustmentInvoicesEnabled && privilege.title === 'finalize-adjustment-invoice') {
              if (this.dataService.canFinalizedAdjustmentInvoices) {
                privilege.configurable = true;
              }

              if (this.isSupportUserCheck(this.user.email)) {
                privilege.permitted = true;
                privilege.configurable = false;
              } else {
                if (userPrivileges[privilege.title]) {
                  privilege.permitted = userPrivileges[privilege.title].permitted;
                }
              }

              this.selectedPrivilege[privilege.title] = privilege;
            }

            if (!this.userId) {
              this.selectedPrivilege[privilege.title] = privilege;
            }

            privilege = { ...privilege };
          });
        }

        if (this.catalogPrivilege) {
          this.catalogPrivilege.forEach((privilege) => {
            if (privilegeMap[privilege.title]) {
              privilege.privilegeId = privilegeMap[privilege.title].privilegeId;
              privilege.configurable = privilegeMap[privilege.title].configurable;
              privilege.permitted = privilegeMap[privilege.title].permitted;
            }

            if (this.isSupportUserCheck(this.user.email)) {
              privilege.permitted = true;
              privilege.configurable = false;
              this.selectedPrivilege[privilege.title] = {
                ...this.selectedPrivilege[privilege.title],
                permitted: true,
                configurable: false,
              };
            } else {
              if (userPrivileges[privilege.title]) {
                privilege.permitted = userPrivileges[privilege.title].permitted;
              }
            }
          });
        }

        if (this.user.role.value === 'admin') {
          Object.keys(this.selectedPrivilege).forEach((key: string) => {
            if (key === 'dashboard' || key === 'reports' || key === 'settings') {
              return (this.selectedPrivilege[key].permitted = true);
            } else {
              return this.selectedPrivilege;
            }
          });
        }

        const defaultSelected = this.helperService.deepCopy(this.selectedPrivilege);
        this.selectedPrivilege = { ...privilegeMap, ...defaultSelected };
      })
      .catch((error) => this.helperService.showToaster('error', error));
  }

  /**
   * If Technician is selected this will return true
   */
  isTechnicianSelected = () => this.user && this.user.role && this.user.role.value === userRoleConsts.tech;

  registerNumberGridClass = () => {
    // if (!this.isTechnicianSelected()) {
    if (this.isCommissionPlanEnabled && !this.isTechnicianSelected()) {
      return 'pr-4 mt-2point5rem';
    }
    if ((this.user && this.user.isWorkAsTechEnabled && !this.isTechnicianSelected()) || (this.isTechnicianSelected() && !this.isCommissionPlanEnabled)) {
      return 'pr-4';
    }
    return '';
    // }
  };

  setPinnedTooltip() {
    this.arAccountIconTooltip = `
      <div class="text-left py-1" style="width: 355px;">
      <div class="font-12 font-bold font-secondary-800 text-white">
        User with this privilege will be able to:
      </div>
      <div class="font-12 font-secondary-800 text-white">
        <ul class="mb-0 pl-15">
          <li>Mark customer as a Charge Accounts</li>
          <li>Change Company Setting Defaults for Charge Account Limits</li>
          <li>Change Customer-specific Charge Account Limits</li>
        </ul>
      </div>
    `;
    this.arAccountIconTooltip += '</div>';
  }

  setPricingTooltip() {
     if(this.dataService.isAttributeBasedPricingEnable){
      this.pricingIconTooltip = `
      <div class="text-left py-1" style="width: 355px;">
      <div class="font-12 font-bold font-secondary-800 text-white">
        User with this privilege will be able to:
      </div>
      <div class="font-12 font-secondary-800 text-white">
        <p class="mb-0">
        Setup and update labor rates, pricing matrices, and overrides. They will also be able to update the cost and price of individual catalog items.
        </p>
      </div>
    `;
    this.pricingIconTooltip += '</div>';
      
     }
     else{

       this.pricingIconTooltip = `
         <div class="text-left py-1" style="width: 355px;">
         <div class="font-12 font-bold font-secondary-800 text-white">
           User with this privilege will be able to:
         </div>
         <div class="font-12 font-secondary-800 text-white">
           <p class="mb-0">
           Setup and update labor rates, pricing matrices. They will also be able to update the cost and price of individual catalog items.
           </p>
         </div>
       `;
       this.pricingIconTooltip += '</div>';
     }
  }

  setPaymentDataTooltip() {
    this.paymentDataTooltip = `
      <div style="width: 300px">
        <div class="pt-2 pl-2 pr-2 mt-2 font-12 font-bold font-secondary-800 text-white">
        Users with this privilege can:
        <ul class="mt-2">
        <li>Edit payment dates</li>
        <li>Edit payment types and methods</li>
        <li>Edit Credit Memo issuance / application dates (for new and existing memos)</li>
        <li>Edit Credit Memo issued / applied amounts (for existing memos)</li>
        </ul>
        </div>
    `;
    this.paymentDataTooltip += '</div>';
  }

  setCatalogDataTooltip() {
    this.catalogInventoryPurchaseTooltip = `
    <div style="width: 380px" class="text-center">
      <div class="mt-2 font-12 font-bold font-secondary-800 text-white">
      Users with this privilege will be able to create inventory
      <div class="mb-2">
    purchase orders for stock replenishment
      </div>
      </div>
  `;
    this.catalogInventoryPurchaseTooltip += '</div>';

    const salesCodeSpecific = `
      <li>Assign Sales Code to Catalog Item</li>
    `;
    const warrantySpecific = `<li>Create/Edit Warranty Settings</li>`;
    this.catalogStockAdjustmentTooltip = `
      <div style="width: 330px" class="text-center">
        <div class="mt-2 font-12 font-bold font-secondary-800 text-white">
        Users with this privilege will be able to manually
        <div class="mb-2">
      increase / decrease item stock counts
        </div>
        </div>
    `;
    this.catalogStockAdjustmentTooltip += '</div>';

    this.catalogCreateUpdateSalesCodeTooltip = `
      <div style="width: 330px" class="text-center">
        <div class="mt-2 font-12 font-bold font-secondary-800 text-white">
        Users with this privilege will be able to create/update
        <div class="mb-2">
          Sales Codes and Sales Groups
        </div>
        </div>
      </div>
    `;

    this.catalogDataTooltip = `
      <div style="width: 300px">
        <div class="pt-2 pl-2 pr-2 mt-2 font-12 font-bold font-secondary-800 text-white">
        Users with this privilege can perform the following actions:
        <ul class="mt-2">
        <li>Create item categories</li>
        <li>Add/Edit Item Classifications for Items</li>
        ${this.isSalesCodeEnabled ? salesCodeSpecific : ''}
        ${this.isPartWarrantiesEnabled ? warrantySpecific : ''}
        </ul>
        </div>
    `;
    this.catalogDataTooltip += '</div>';
  }

  addNewRestrictedIp() {
    this.restrictedIps.push({ ip: '', isValid: true, error: '' });
  }

  removeRestrictedIp(index: number) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Remove ip`,
        message: `This ip will be removed. Are you sure you want to remove it?`,
        button: {
          success: { title: 'Confirm' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.restrictedIps.splice(index, 1);
      }
      onClose.unsubscribe();
    });
  }

  handleIpChange(index: number, value: any) {
    const ip = value;
    this.restrictedIps[index].ip = ip;
    this.restrictedIps[index].isValid = this.validateIp(ip);
    this.restrictedIps[index].error = this.restrictedIps[index].isValid ? '' : 'Invalid IP Address';
  }

  validateIp(ip: string): boolean {
    const ipRegex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?))$|^([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(:0{1,4}){0,1}:){0,1}(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])\.(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])\.(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])\.(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])|([0-9a-fA-F]{1,4}:){1,4}:(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])\.(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])\.(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])\.(?:25[0-5]|(2[0-4]|1{0,1}[0-9])?[0-9])$/;
    return ipRegex.test(ip);
  }

  handleFocusIp(index: number) {
    this.restrictedIps[index].error = '';
  }

  handleBlurIp(index: number) {
    const ip = this.restrictedIps[index].ip;
    if (!ip) {
      this.restrictedIps[index].error = 'IP Address is required';
    } else if (!this.validateIp(ip)) {
      this.restrictedIps[index].error = 'Invalid IP Address';
    }
  }
}
